import {
  Divider,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  MenuItem,
  Button,
  Autocomplete,
  Box,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import dayjs from 'dayjs';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import {
  FormProvider,
  Iconify,
  RHFDatePicker,
  RHFSelect,
  RHFTextField,
  RHFUploadAvatar,
  Scrollbar,
} from '@app/components';
import { StyledField, StyledInputs } from '../style';
import { IAssetSchema, IAssets } from '../types';
import { addAssetSchema } from '../validators';
import { selectAddAssetState, selectUpdateAssetState, selectVendorsState } from '../selectors';
import { useDialog, useRequestState } from '@app/hooks';
import { addAsset, getVendors, updateAsset } from '../slice';
import { AddVender } from './add-vender';
import { selectEmployeesState, getEmployees } from '@app/modules/employees';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: IAssets;
}

const AddAssets = ({ open, onClose, row }: Props) => {
  const dispatch = useDispatch();
  const addVenderDialog = useDialog();
  const methods = useForm<IAssetSchema>({
    resolver: yupResolver(addAssetSchema) as any,
    defaultValues: {
      assetName: get(row, 'assetName', ''),
      otherAssetName: get(row, 'otherAssetName', ''),
      manufacturer: get(row, 'manufacturer', ''),
      purchaseDate: get(row, 'purchaseDate', ''),
      warranty: get(row, 'warranty', ''),
      cost: get(row, 'cost', ''),
      condition: get(row, 'condition', ''),
      status: get(row, 'status', ''),
      supplier: get(row, 'supplier', ''),
      serialNumber: get(row, 'serialNumber', ''),
      model: get(row, 'model', ''),
      description: get(row, 'description', ''),
      gst: get(row, 'gst', ''),
      assetsImage: row?.image ? row.image : '',
      assetUser: get(row, 'assetUser'),
      vendor: get(row, 'vendorId', ''),
    },
  });
  const [isImageUploaded, setIsImageUploaded] = useState(false);

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        methods.setValue('assetsImage', newFile, { shouldValidate: true });
        setIsImageUploaded(true);
      }
    },
    [methods]
  );

  const images = useWatch({ control: methods.control, name: 'assetsImage' });
  const assetName = useWatch({ control: methods.control, name: 'assetName' });
  const assetUser = useWatch({ control: methods.control, name: 'assetUser' });

  const { loading } = useRequestState({
    stateSelector: row ? selectUpdateAssetState : selectAddAssetState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });

  const onSubmit = (form: IAssetSchema) => {
    row
      ? dispatch(
          updateAsset({ ...form, id: row?.id, assetsImage: methods.getValues('assetsImage') })
        )
      : dispatch(addAsset({ ...form, assetsImage: methods.getValues('assetsImage') }));
  };
  const { data } = useRequestState({
    stateSelector: selectVendorsState,
  });

  const { data: AssetUser } = useRequestState({
    stateSelector: selectEmployeesState,
  });

  const [query, setQuery] = useState();

  useEffect(() => {
    dispatch(getVendors(query));
    dispatch(getEmployees());
  }, [dispatch, query]);

  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open}>
          <Header
            variant="dialog"
            title={row ? Strings.assets.updateAssets : Strings.assets.addAsset}
            onClose={onClose}
          />
          <Divider />
          <Scrollbar>
            <DialogContent>
              <StyledInputs>
                <Stack rowGap={2} gap={2} direction="row" pt={2}>
                  <RHFSelect
                    required
                    name="assetName"
                    type="select"
                    sx={{ width: '50%' }}
                    label={Strings.assets.assetName}
                  >
                    <MenuItem value="mobile">{Strings.assets.mobile}</MenuItem>
                    <MenuItem value="laptop">{Strings.assets.laptop}</MenuItem>
                    <MenuItem value="other">{Strings.assets.other}</MenuItem>
                  </RHFSelect>
                  {assetName === 'other' && (
                    <RHFTextField
                      sx={{ width: '50%' }}
                      required
                      name="otherAssetName"
                      label={Strings.assets.otherAssetName}
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  <RHFDatePicker
                    sx={{ width: '50%' }}
                    required
                    label={Strings.assets.purchaseDate}
                    name="purchaseDate"
                    fullWidth
                    defaultValue={row ? dayjs(row.purchaseDate, 'DD/MM/YYYY') : null}
                  />
                </Stack>
              </StyledInputs>
              <Stack direction="row" gap={2} pt={2} pr={2}>
                <Stack direction={'row'} gap={1} flex={1}>
                  <RHFTextField name="gst" label={Strings.assets.gst} fullWidth />
                </Stack>
                <Stack flex={1}>
                  <Autocomplete
                    sx={{ width: '100%' }}
                    defaultValue={row?.users?.firstName}
                    options={
                      AssetUser?.records
                        ?.filter((record) => record.roleId !== 1)
                        .map((option: any) => ({
                          label: option.firstName,
                          id: option.id,
                        })) ?? []
                    }
                    onChange={(event, value) => {
                      methods.setValue('assetUser', value?.id);
                    }}
                    renderInput={(params: any) => (
                      <RHFTextField
                        {...params}
                        name="assetUser"
                        label={Strings.assets.assetUser}
                        fullWidth
                      />
                    )}
                  />
                </Stack>
              </Stack>
              <Stack direction="row" gap={2} pt={2} pr={2}>
                <Stack direction={'row'} gap={1} flex={1}>
                  <Autocomplete
                    sx={{ width: '100%' }}
                    defaultValue={row?.vendors?.name}
                    options={
                      data?.map((option: any) => ({ label: option.name, id: option.id })) ?? []
                    }
                    onChange={(event, value) => {
                      if (value) {
                        methods.setValue('vendor', value?.id);
                      }
                    }}
                    renderInput={(params: any) => (
                      <RHFTextField
                        required
                        name="vendor"
                        label={Strings.assets.vendor}
                        variant="outlined"
                        {...params}
                        onChange={(event: any) => {
                          setQuery(event.target.value);
                        }}
                      />
                    )}
                  />
                  <Button
                    variant="outlined"
                    color="inherit"
                    sx={{
                      borderRadius: '8px',
                      width: '50px',
                      height: '55px',
                      minWidth: '20px',
                    }}
                    onClick={addVenderDialog.show}
                  >
                    <Iconify icon="ic:outline-plus" />
                  </Button>
                </Stack>
                <Stack flex={1}>
                  <RHFTextField
                    required
                    label={Strings.assets.manufacturer}
                    name="manufacturer"
                    fullWidth
                  />
                </Stack>
              </Stack>
              <StyledInputs>
                <Stack rowGap={2} gap={2} direction="row" flexWrap="wrap" pt={2}>
                  <RHFTextField label={Strings.assets.model} name="model" fullWidth />
                  <RHFTextField
                    required
                    label={Strings.assets.serialNumber}
                    name="serialNumber"
                    fullWidth
                  />
                  <RHFTextField
                    required
                    label={Strings.assets.condition}
                    name="condition"
                    fullWidth
                  />
                  <RHFTextField
                    required
                    label={Strings.assets.warranty}
                    name="warranty"
                    fullWidth
                  />
                  <RHFTextField required label={Strings.assets.cost} name="cost" fullWidth />
                  <RHFSelect required name="status" type="select" label={Strings.assets.status}>
                    <MenuItem value="Damaged" disabled={!!assetUser}>
                      {Strings.assets.damaged}
                    </MenuItem>
                    <MenuItem value="Available" disabled={!!assetUser}>
                      {Strings.assets.available}
                    </MenuItem>
                    <MenuItem value="Assigned">{Strings.assets.assigned}</MenuItem>
                    <MenuItem value="Repairing" disabled>
                      {Strings.assets.repairing}
                    </MenuItem>
                    <MenuItem value="Repaired" disabled>
                      {Strings.assets.repaired}
                    </MenuItem>
                  </RHFSelect>
                </Stack>
              </StyledInputs>
              <StyledField
                sx={{
                  '@media (max-width: 768px)': {
                    paddingRight: '13px',
                  },
                }}
              >
                <Stack gap={2} direction="column" pt={2}>
                  <Box display="flex" marginTop={2} flexWrap={'wrap'}>
                    {Array.isArray(images) &&
                      images.map((file: any, index: number, array: any[]) => (
                        <>
                          {index < array.length - 1 && (
                            <img
                              src={file?.preview ? file?.preview : file}
                              alt={`Preview ${index}`}
                              style={{
                                width: 115,
                                height: 115,
                                margin: '5px',
                                borderRadius: '10px',
                              }}
                            />
                          )}
                        </>
                      ))}
                    <RHFUploadAvatar
                      multiple
                      name={'assetsImage'}
                      onDrop={handleDrop}
                      sx={{
                        borderRadius: '10px',
                        width: 115,
                        height: 115,
                        border: 'none',
                        m: '5px',
                      }}
                      placeholderSx={{
                        borderRadius: '10px',
                        width: '100%',
                        height: '100%',
                        border:
                          isImageUploaded || row?.assetsImage
                            ? 'none'
                            : '5px dashed rgba(0, 0, 0, 0.08)',
                      }}
                    />
                  </Box>
                  <RHFTextField
                    name="description"
                    label={Strings.field.description}
                    multiline
                    minRows={3}
                    fullWidth
                  />
                </Stack>
              </StyledField>
            </DialogContent>
          </Scrollbar>
          <Divider sx={{ pt: 2 }} />
          <DialogActions>
            <Button variant="text" color="inherit" onClick={onClose}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={loading}
              onClick={methods.handleSubmit(onSubmit)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </FormProvider>
      {addVenderDialog.visible && <AddVender open onClose={addVenderDialog.hide} />}
    </>
  );
};

export default AddAssets;
